import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

function Shell({ title, children }) {
  return (
    <div className="min-h-screen w-full" style={{ background: "radial-gradient(ellipse at top, #1F2D7A 0%, #0B1437 55%, #050A1C 100%)" }}>
      <div className="mx-auto max-w-[720px] px-4 py-8">
        <Link to="/login" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white mb-4" data-testid="legal-back">
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>
        <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-9">
          <div className="flex items-center gap-2.5 mb-6">
            <img src="/curtron-emblem.png" alt="Curtron" className="h-6 w-auto" />
            <div>
              <div className="font-heading font-bold text-curtron-ink">Curtron Hub</div>
              <div className="text-[11px] text-curtron-muted">a service of Curtron Transport Services</div>
            </div>
          </div>
          <h1 className="font-heading text-2xl font-bold text-curtron-ink tracking-tight">{title}</h1>
          <p className="text-xs text-curtron-muted mt-1 mb-5">Effective June 1, 2026</p>
          <div className="prose-legal space-y-4 text-sm text-curtron-sub leading-relaxed">{children}</div>
          <div className="mt-8 pt-4 border-t border-curtron-line text-xs text-curtron-muted">
            © 2026 Curtron Transport Services. All rights reserved. ·{" "}
            <a href="mailto:support@curtronhub.com" className="text-curtron-green">support@curtronhub.com</a>
          </div>
        </div>
      </div>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-heading font-semibold text-curtron-ink text-base mt-5">{children}</h2>;

export default function Privacy() {
  return (
    <Shell title="Privacy Policy">
      <p>Curtron Transport Services ("Curtron," "we," "us") operates the Curtron Hub mobile and web dispatch application. This Privacy Policy explains what information we collect, how we use it, and your choices. Curtron Hub is a private application used by authorized staff and contracted drivers; it is not a consumer product.</p>

      <H>Information We Collect</H>
      <p>Account details (name, email, role, region, assigned vehicle), transport records you create or are assigned to, GPS location captured while a driver is actively working a transport, and, if you opt in, your mobile phone number for SMS dispatch alerts.</p>

      <H>SMS &amp; Mobile Information</H>
      <p><strong>We never sell or share your mobile phone number or SMS opt-in status with third parties for marketing or promotional purposes.</strong> Mobile information is used solely to deliver operational dispatch notifications you have requested.</p>
      <ul className="list-disc pl-5 space-y-1">
        <li>Drivers opt in inside the app (Profile → "Receive dispatch alerts by text message"). Consent is not a condition of employment.</li>
        <li>Messages are one-time operational alerts sent when a transport is assigned to you (agency, pickup address, scheduled time).</li>
        <li>Message frequency varies (typically 1–5 per week). Message &amp; data rates may apply.</li>
        <li>Reply <strong>STOP</strong> to any message to unsubscribe at any time, or <strong>HELP</strong> for assistance.</li>
      </ul>

      <H>How We Use Information</H>
      <p>To coordinate and track transports, generate run-sheet paperwork, communicate with your team, send dispatch alerts you opted into, and maintain operational and compliance records.</p>

      <H>Data Sharing</H>
      <p>We share information only with our SMS delivery provider (Telnyx) strictly to send the alerts you requested, and where required by law. We do not sell personal information.</p>

      <H>Data Retention &amp; Security</H>
      <p>Records are retained as required for operational and legal purposes and protected with industry-standard safeguards. You may request account deletion in-app (Profile → "Delete my account").</p>

      <H>Your Choices</H>
      <p>You can opt out of SMS at any time (reply STOP or turn off the setting in Profile), update your profile details, or delete your account.</p>

      <H>Governing Law &amp; Contact</H>
      <p>This policy is governed by the laws of the State of Georgia, USA. Questions? Contact <a href="mailto:support@curtronhub.com" className="text-curtron-green">support@curtronhub.com</a>.</p>
    </Shell>
  );
}
